import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { forEach } from 'lodash';
import { WarningFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getIsAmazonAppStore, getIsGooglePlayStore, isGooglePlatform } from '@/helpers';
import { displayConsoleNameForAppStoreType } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';
import { formatDateWithTimezone } from '@/timeHelpers';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType } from '@/types';
import { AppBuildComponent, CIBuildComponent } from '../BuildComponents';
import LinkageIcon from '../icons/LinkageIcon';
import { runwayColors } from '../styles/globalStyles';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

export interface AppStoreBuildSelectProps {
    appId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    availableBuilds: AppStoreAvailableBuild[];
    includeAssociatedCIBuilds: boolean;
    multiple: boolean;
    showRecentBuilds: boolean;
    showPastBuildsEmptyState: boolean;
    showsClearButton?: boolean;
    placeholder?: string;
    loading?: boolean;
    disabled?: boolean;
    notFoundContent?: React.ReactNode;
    // Builds picked elsewhere are shown disabled, so the same build can't be picked twice
    excludedBuildIds?: string[];
    excludeReason?: string;
    // Supplied by the wrapping Form.Item
    value?: string | string[];
    onChange?: (value: string | string[]) => void;
}

export const AppStoreBuildSelect: React.FC<AppStoreBuildSelectProps> = (props) => {
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const extraBuildInfo = (appStoreBuild: AppStoreAvailableBuild) => {
        if (appStoreBuild.isCurrentlySelected && appStoreBuild.isNewestAppStoreBuild) {
            return <Text>(latest, selected)</Text>;
        } else if (appStoreBuild.isCurrentlySelected) {
            return <Text>(currently selected)</Text>;
        } else if (appStoreBuild.isNewestAppStoreBuild) {
            return <Text>(latest)</Text>;
        }

        return null;
    };

    const renderAvailableBuild = (
        availableBuild: AppStoreAvailableBuild,
        includeAssociatedCIBuild: boolean,
        forceDisabled: boolean,
        excludeReason?: string,
    ) => {
        const isInternalOnly = !!availableBuild.appStoreBuild.isInternal;

        const baseBuildInfo = (
            <Space>
                {isInternalOnly && <WarningFilled style={{ color: runwayColors.statusYellow }} />}
                <AppBuildComponent
                    integrationId={availableBuild.appStoreBuild.integrationId}
                    buildIdentifier={availableBuild.appStoreBuild.buildNumber}
                    isProcessed={availableBuild.appStoreBuild.isProcessed}
                />
                {includeAssociatedCIBuild && (
                    <>
                        <LinkageIcon width={16} height={16} />
                        {availableBuild.associatedCIBuild && (
                            <CIBuildComponent
                                integrationId={availableBuild.associatedCIBuild.integrationId}
                                buildIdentifier={availableBuild.associatedCIBuild.buildIdentifier}
                            />
                        )}
                        {!availableBuild.associatedCIBuild && <Text type="secondary">Unknown</Text>}
                        {extraBuildInfo(availableBuild)}
                        {availableBuild.associatedCIBuild?.commitMessage && (
                            <Text type="secondary" ellipsis>
                                - {availableBuild.associatedCIBuild.commitMessage}
                            </Text>
                        )}
                    </>
                )}
            </Space>
        );

        const optionDisabled =
            forceDisabled || !!excludeReason || !availableBuild.appStoreBuild.isProcessed || isInternalOnly;

        const uploadTimestamp =
            getIsGooglePlayStore(props.appStoreType) && availableBuild.appStoreBuild.uploadedAt ?
                formatDateWithTimezone(
                    availableBuild.appStoreBuild.uploadedAt,
                    'short',
                    effectiveTimezone,
                    use24HourTimeFormat,
                )
            :   null;

        const rowTooltipTitle =
            excludeReason ? excludeReason
            : isInternalOnly ?
                `This build is marked as "Internal Only" in App Store Connect and can't be submitted for App Store review.`
            : uploadTimestamp ? `Uploaded ${uploadTimestamp}`
            : null;

        return (
            <Select.Option
                key={availableBuild.appStoreBuild.id}
                value={availableBuild.appStoreBuild.id}
                disabled={optionDisabled}
                label={baseBuildInfo}
            >
                <Space direction="vertical" size={0} style={{ opacity: optionDisabled ? 0.5 : 1.0 }}>
                    {rowTooltipTitle ?
                        <Tooltip title={rowTooltipTitle} placement="topLeft">
                            {baseBuildInfo}
                        </Tooltip>
                    :   baseBuildInfo}
                    {availableBuild.tracks && availableBuild.tracks.length > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            - Found on tracks: {availableBuild.tracks.join(', ')}
                        </Text>
                    )}
                </Space>
            </Select.Option>
        );
    };

    const recentBuilds = [];
    const previousBuilds = [];

    forEach(props.availableBuilds, (build: AppStoreAvailableBuild) => {
        if (build.isRecentBuild) {
            recentBuilds.push(build);
        } else {
            previousBuilds.push(build);
        }
    });

    // Past builds for Amazon are the builds fetched from the Amazon Appstore, and we can't actually interact with them
    const shouldDisablePastBuildSelection = getIsAmazonAppStore(props.appStoreType);

    const excludeReasonForBuild = (build: AppStoreAvailableBuild) =>
        props.excludedBuildIds?.includes(build.appStoreBuild.id) ? props.excludeReason : null;

    return (
        <Select
            allowClear={props.showsClearButton}
            placeholder={props.placeholder ?? 'Select'}
            mode={props.multiple ? 'multiple' : null}
            optionLabelProp="label"
            loading={props.loading}
            disabled={props.disabled}
            notFoundContent={props.notFoundContent}
            value={props.value}
            onChange={props.onChange}
        >
            {props.showRecentBuilds && (
                <Select.OptGroup label={buildsLabelForPlatform(props.platform, true, props.appStoreType)}>
                    {recentBuilds.map((build) =>
                        renderAvailableBuild(
                            build,
                            props.includeAssociatedCIBuilds,
                            false,
                            excludeReasonForBuild(build),
                        ),
                    )}
                    {recentBuilds.length === 0 && (
                        <Select.Option disabled>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                No recent builds
                            </Text>
                        </Select.Option>
                    )}
                </Select.OptGroup>
            )}
            {(props.showPastBuildsEmptyState || previousBuilds.length !== 0) && (
                <Select.OptGroup label={buildsLabelForPlatform(props.platform, false, props.appStoreType)}>
                    {previousBuilds.map((build) =>
                        renderAvailableBuild(
                            build,
                            props.includeAssociatedCIBuilds,
                            shouldDisablePastBuildSelection,
                            excludeReasonForBuild(build),
                        ),
                    )}
                    {props.showPastBuildsEmptyState && previousBuilds.length === 0 && (
                        <Select.Option disabled>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                No recent builds
                            </Text>
                        </Select.Option>
                    )}
                </Select.OptGroup>
            )}
        </Select>
    );
};

const buildsLabelForPlatform = (platform: AppPlatform, isRecent: boolean, appStoreType?: AppStoreType) => {
    let tooltipText = null;
    let labelText = isRecent ? 'Recent builds' : 'Past builds';

    if (isGooglePlatform(platform)) {
        const isAmazonAppStore = getIsAmazonAppStore(appStoreType);
        const storeText = displayConsoleNameForAppStoreType(appStoreType);

        if (isAmazonAppStore) {
            labelText = isRecent ? 'Recent builds' : `${storeText} builds`;
            tooltipText =
                isRecent ?
                    `Recent builds available for upload to the ${storeText}`
                :   `Version codes associated with the Upcoming Version edit in the ${storeText}`;
        } else {
            tooltipText =
                isRecent ?
                    `Version codes higher than that of the last release in ${storeText}`
                :   `Version codes equal to or less than that of the last release in ${storeText}`;
        }
    }

    return (
        <Space align="center">
            <Text>{labelText}</Text>
            {tooltipText && <TooltipInfoIcon tooltipContent={tooltipText} />}
        </Space>
    );
};
